import * as Yup from 'yup'
import {
  transformNumberValue,
  transformNumberValueAllowZero,
} from '../../../../shared/transformNumberValue'
import { testNumberWithDotLength } from '../../../../shared/testNumberWithDotLength'
import { IMaterialRow, IPackagingResourceSource } from './types'

// D3: packaging materials still travel over the wire as the same shared taxonomy-reference shape
// food's Ingredients use (InputIngredientType/InputIngredientId/InputIngredientTaxonomyLevel) — no
// PackagingMaterialCategoryId/FunctionalUnitId fields exist BE-side. These two functions are the
// only place that boundary gets crossed: the FE's own Category+Material vocabulary stays local to
// the form, translated to/from the BE shape on load and on submit.
const PACKAGING_MATERIAL_INPUT_TYPE = 4 // InputIngredientTypeEnum.PackagingMaterial
const CATEGORY_TAXONOMY_LEVEL = 1 // InputIngredientTaxonomyLevelType.CategoryOrFamily
const FUNCTIONAL_UNIT_TAXONOMY_LEVEL = 3 // InputIngredientTaxonomyLevelType.FunctionalUnitOrBrick

export const mapIngredientToMaterialRow = (ingredient: any): IMaterialRow => ({
  id: ingredient.id,
  packagingMaterialCategoryId:
    ingredient.inputIngredientTaxonomyLevel === CATEGORY_TAXONOMY_LEVEL
      ? ingredient.inputIngredientId
      : null,
  packagingMaterialFunctionalUnitId:
    ingredient.inputIngredientTaxonomyLevel === FUNCTIONAL_UNIT_TAXONOMY_LEVEL
      ? ingredient.inputIngredientId
      : null,
  quantity: ingredient.quantity,
  quantityNotes: ingredient.quantityNotes,
})

export const mapMaterialRowToIngredient = (row: IMaterialRow) => ({
  id: row.id,
  inputIngredientType: PACKAGING_MATERIAL_INPUT_TYPE,
  inputIngredientId: row.packagingMaterialFunctionalUnitId ?? row.packagingMaterialCategoryId,
  inputIngredientTaxonomyLevel: row.packagingMaterialFunctionalUnitId
    ? FUNCTIONAL_UNIT_TAXONOMY_LEVEL
    : CATEGORY_TAXONOMY_LEVEL,
  quantity: row.quantity,
  quantityNotes: row.quantityNotes,
})

// Same boundary-translation problem on the Outputs side: BE's wire shape is a single `Component`
// (ProductQuantityDTO: outputProductId/outputProductType/productType/quantity/economicAllocation),
// not the flat packagingComponentId/componentScalingFactor the form uses. D4/D5: OutputProductType
// is always PackagingComponent, ProductType is always MainProduct, EconomicAllocation is fixed at
// 100 (no co-products) — none of those three are user-editable, so they're hardcoded here, not
// read from form state.
const PACKAGING_COMPONENT_OUTPUT_TYPE = 4 // OutputProductTypeEnum.PackagingComponent
const MAIN_PRODUCT_TYPE = 0 // ProductTypeEnum.MainProduct

export const mapProductToComponentState = (component: any) => ({
  packagingComponentId: component?.outputProductId ?? null,
  componentScalingFactor: component?.quantity ?? 100,
  componentId: component?.id,
})

export const mapComponentStateToProduct = (values: {
  packagingComponentId: number | null
  componentScalingFactor: number
  componentId?: number
}) =>
  values.packagingComponentId
    ? {
        id: values.componentId,
        outputProductId: +values.packagingComponentId,
        outputProductType: PACKAGING_COMPONENT_OUTPUT_TYPE,
        productType: MAIN_PRODUCT_TYPE,
        quantity: values.componentScalingFactor ? +values.componentScalingFactor : 0,
        economicAllocation: 100,
      }
    : null

export const resourceSchema = Yup.object().shape({
  resourceSourceId: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Please select ResourceSourceId')
    .required('ResourceSourceId is required'),
  quantity: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Quantity is required')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  correspondingProcess: Yup.string().nullable(true),
})

export const wasteSchema = Yup.object().shape({
  resourceSourceId: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Please select Waste')
    .required('Waste is required'),
  quantity: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Quantity is required')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
})

export const elementaryFlowSchema = Yup.object().shape({
  resourceSourceId: Yup.number()
    .transform(transformNumberValue)
    .typeError('Field accepts numbers only')
    .min(0, 'Please select an elementary flow')
    .required('Elementary flow is required'),
  quantity: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Quantity is required')
    .test('len', 'Max symbols allowed - 28. Max symbols after dot - 16', (value, config: any) => {
      const val = config.options.originalValue ? config.options.originalValue : 0
      return (
        (!val.toString().includes('.') ||
          (val.toString().includes('.') && val.toString().split('.')[1].length < 17)) &&
        val.toString().length < 29
      )
    }),
})

export const formSchema = Yup.object().shape({
  // activityName intentionally has no rule — it's derived/display-only (BE plan O1), not a real
  // stored field. BE has no matching DTO property; requiring it here caused a false "required"
  // block with no way to satisfy it once the value stopped round-tripping.
  processFlow: Yup.string().required('Process flow field is required!'),
  referenceTechnologyType: Yup.string().required('Reference technology type field is required!'),
  secondaryDataReferenceSourceType: Yup.number()
    .typeError('Please select Secondary Data Reference Source Type')
    .integer()
    .nullable(false),
  referenceSourcingCountry: Yup.number()
    .typeError('Please select Reference Sourcing Country')
    .integer()
    .nullable(true),
  referenceRegionIdentifier: Yup.number()
    .typeError('Please select Reference Region Identifier')
    .integer()
    .nullable(false),
  referenceDataPublicationYear: Yup.number()
    .typeError('Please select Reference Data Publication Year')
    .min(1900, 'Reference Data Publication Year can`t be less than 1900')
    .max(2200, 'Reference Data Publication Year can`t be greater than 2200')
    .nullable(false),
  referencesSamplingYear: Yup.number()
    .typeError('Please select Sampling End Date')
    .min(1900, 'Sampling End Date can`t be less than 1900')
    .max(2200, 'References Sampling Year can`t be greater than 2200')
    .nullable(true),
  referenceTechnologyProxyContribution: Yup.number()
    .typeError('Please select Reference Technology Proxy Contribution')
    .min(0, 'Reference Technology Proxy Contribution can`t be less than 0')
    .max(100, 'Reference Technology Proxy Contribution can`t be greater than 100')
    .nullable(false),
  defaultMaterialLossFactor: Yup.number()
    .typeError('Value must be a number')
    .required('Default material loss factor is required')
    .test('defaultMaterialLossFactor', 'The value cannot be 0', function (value) {
      if (value === 0) {
        return this.createError({
          path: 'defaultMaterialLossFactor',
          message: 'Default material loss factor cannot be 0',
        })
      }
      return true
    }),
  // resourceSources and waste have no rules: their sections are no longer shown, so their rows
  // can't be corrected.
  elementaryFlows: Yup.array().of(elementaryFlowSchema),
  outputElementaryFlows: Yup.array().of(elementaryFlowSchema),
})

// Collapsed rows showed the bare quantity, so "2" gave no clue whether it meant kg or kWh. The
// expanded row already carries the unit as the Quantity addon, and the Materials trigger already
// appends "Kg" — this keeps Resources/Elementary flows/Waste reading the same way.
export const buildFlowTriggerLabel = (
  options: IPackagingResourceSource[],
  resourceSourceId?: number | null,
  quantity?: number | string | null
): string => {
  const source = options.find(el => el.id === resourceSourceId)
  const unit = source?.unit ? ` ${source.unit}` : ''

  return `${source?.combinedName} - ${quantity}${unit}`
}
